import type { RootState } from '..';
import type { LumiEditorState } from '../lumi-editor/types';
import type {
  UserTokenUsage,
  WorksheetChanges,
  WorksheetChatState,
  WorksheetChatMessage,
} from './types';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { apolloClient } from 'src/lib/apollo-client';

import {
  GET_USER_TOKEN_USAGE_QUERY,
  GET_WORKSHEET_CHAT_HISTORY_QUERY,
  WORKSHEET_CHAT_SEND_MESSAGE_MUTATION,
} from './graphql';

const initialState: WorksheetChatState = {
  messages: [],
  isOpen: false,
  isSending: false,
  error: null,
  isLoadingResponse: false,
  tokenUsage: null,
};

// Async thunks
export const loadChatHistory = createAsyncThunk(
  'worksheetChat/loadHistory',
  async (worksheetId: string, { rejectWithValue }) => {
    try {
      const { data } = await apolloClient.query<{
        getWorksheetChatHistory: WorksheetChatMessage[];
      }>({
        query: GET_WORKSHEET_CHAT_HISTORY_QUERY,
        variables: { worksheetId },
        fetchPolicy: 'network-only',
      });

      if (!data) {
        throw new Error('No data returned from server');
      }

      return data.getWorksheetChatHistory as WorksheetChatMessage[];
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load chat history');
    }
  }
);

export const sendChatMessage = createAsyncThunk<
  { message: WorksheetChatMessage; changes?: WorksheetChanges; tokenUsage: UserTokenUsage },
  { worksheetId: string; message: string; worksheetState: LumiEditorState },
  { state: RootState }
>(
  'worksheetChat/sendMessage',
  async ({ worksheetId, message, worksheetState }, { rejectWithValue }) => {
    try {
      // Prepare the worksheet state for the API
      const stateInput = {
        title: worksheetState.title,
        content: JSON.stringify(worksheetState.content),
        structure: worksheetState.structure,
      };

      const { data } = await apolloClient.mutate<{
        worksheetChatSendMessage: {
          message: WorksheetChatMessage;
          changes?: string;
          tokenUsage: UserTokenUsage;
        };
      }>({
        mutation: WORKSHEET_CHAT_SEND_MESSAGE_MUTATION,
        variables: {
          input: {
            worksheetId,
            message,
            worksheetState: stateInput,
          },
        },
      });

      const result = data?.worksheetChatSendMessage;
      if (!result) {
        throw new Error('No response from server');
      }

      const changes = result.changes ? JSON.parse(result.changes) : undefined;

      return {
        message: result.message,
        changes,
        tokenUsage: result.tokenUsage,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

export const loadUserTokenUsage = createAsyncThunk(
  'worksheetChat/loadTokenUsage',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await apolloClient.query<{
        getWorksheetChatUserTokenUsage: UserTokenUsage;
      }>({
        query: GET_USER_TOKEN_USAGE_QUERY,
        fetchPolicy: 'network-only',
      });

      if (!data) {
        throw new Error('No data returned from server');
      }

      return data.getWorksheetChatUserTokenUsage;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to load token usage');
    }
  }
);

const worksheetChatSlice = createSlice({
  name: 'worksheetChat',
  initialState,
  reducers: {
    toggleChat: (state) => {
      state.isOpen = !state.isOpen;
    },
    openChat: (state) => {
      state.isOpen = true;
    },
    closeChat: (state) => {
      state.isOpen = false;
    },
    clearMessages: (state) => {
      state.messages = [];
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Load chat history
      .addCase(loadChatHistory.pending, (state) => {
        state.error = null;
      })
      .addCase(loadChatHistory.fulfilled, (state, action) => {
        state.messages = action.payload;
        state.isLoadingResponse = false; // Clear loading state when history is loaded
      })
      .addCase(loadChatHistory.rejected, (state, action) => {
        state.error = action.payload as string;
        state.isLoadingResponse = false;
      })
      // Send message
      .addCase(sendChatMessage.pending, (state, action) => {
        state.isSending = true;
        state.error = null;
        state.isLoadingResponse = true;

        // Optimistically add the user's message immediately
        const tempUserMessage: WorksheetChatMessage = {
          id: `temp-${Date.now()}`,
          worksheet_id: action.meta.arg.worksheetId,
          user_id: 'current-user', // Will be replaced by backend response
          role: 'user',
          message: action.meta.arg.message,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        state.messages.push(tempUserMessage);
      })
      .addCase(sendChatMessage.fulfilled, (state, action) => {
        state.isSending = false;
        // Keep isLoadingResponse true until we reload the history
        // It will be set to false when loadChatHistory completes

        // Update token usage from the response
        state.tokenUsage = action.payload.tokenUsage;

        // Don't add messages here - we'll reload the full history from the backend
        // to get both the user message and assistant message with proper IDs
      })
      .addCase(sendChatMessage.rejected, (state, action) => {
        state.isSending = false;
        state.isLoadingResponse = false;
        state.error = action.payload as string;

        // Remove the optimistic message on error
        state.messages = state.messages.filter((msg) => !msg.id.startsWith('temp-'));
      })
      // Load token usage
      .addCase(loadUserTokenUsage.fulfilled, (state, action) => {
        state.tokenUsage = action.payload;
      })
      .addCase(loadUserTokenUsage.rejected, (state, action) => {
        // Silently fail - token usage is not critical
        console.error('Failed to load token usage:', action.payload);
      });
  },
});

export const { toggleChat, openChat, closeChat, clearMessages, clearError } =
  worksheetChatSlice.actions;

// Selectors
export const selectChatMessages = (state: RootState) => state.worksheetChat.messages;
export const selectIsChatOpen = (state: RootState) => state.worksheetChat.isOpen;
export const selectIsSending = (state: RootState) => state.worksheetChat.isSending;
export const selectChatError = (state: RootState) => state.worksheetChat.error;
export const selectIsLoadingResponse = (state: RootState) => state.worksheetChat.isLoadingResponse;
export const selectTokenUsage = (state: RootState) => state.worksheetChat.tokenUsage;

export default worksheetChatSlice.reducer;
